/* ==========================================================================
   Slider Banner

   Drives the hero slider built by sections/slider-banner.liquid.

   Rotation is not on a fixed interval: a video slide advances when its video
   finishes, an image slide advances after its own configured duration. That
   keeps each slide on screen for as long as its own content needs.

   Self-contained: touches nothing outside [data-slider-banner]. Swiper is
   already loaded globally on the homepage/index templates
   (snippets/conditional_styles.liquid); if it is missing the script pulls the
   same build the theme already uses, and if that fails too the hero degrades
   to a static first slide.
   ========================================================================== */

   (function () {
    'use strict';
  
    // The section tag renders one <script> per section instance. Bail out if a
    // second copy executes.
    if (window.sliderBannerLoaded) return;
    window.sliderBannerLoaded = true;
  
    var SWIPER_SRC = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
    var MOBILE_QUERY = '(max-width: 768px)';
    var STALL_GUARD_MS = 15000;
  
    var instances = {};
    var swiperLoader = null;
  
    function loadSwiper() {
      if (typeof window.Swiper === 'function') return Promise.resolve();
      if (swiperLoader) return swiperLoader;
  
      swiperLoader = new Promise(function (resolve, reject) {
        var tag = document.createElement('script');
        tag.src = SWIPER_SRC;
        tag.async = true;
        tag.onload = resolve;
        tag.onerror = reject;
        document.head.appendChild(tag);
      });
  
      return swiperLoader;
    }
  
    function toArray(list) {
      return Array.prototype.slice.call(list || []);
    }
  
    /* ------------------------------------------------------------------------
       Hero
       ------------------------------------------------------------------------ */
  
    function Hero(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-slider-banner-viewport]');
      this.dots = toArray(root.querySelectorAll('[data-slider-banner-dot]'));
      this.total = parseInt(root.getAttribute('data-count'), 10) || 1;
      this.autoRotate = root.getAttribute('data-autoplay') === 'true' && this.total > 1;
      this.speed = parseInt(root.getAttribute('data-speed'), 10) || 500;
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.timer = null;
      this.paused = false;
      // Set only while a block is selected in the theme editor.
      this.editorHold = false;
  
      if (!this.viewport) return;
  
      this.onSlideChange = this.onSlideChange.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
  
      this.initSwiper();
      this.bind();
  
      // With an observer the first slide is started when the hero scrolls into
      // view, so starting it here as well would restart playback immediately.
      if (!this.observer) this.onSlideChange();
    }
  
    Hero.prototype.initSwiper = function () {
      this.swiper = new window.Swiper(this.viewport, {
        // Namespaced wrapper/slide classes keep this slider invisible to the
        // theme's own carousel init, which looks for .swiper-wrapper/.swiper-slide.
        wrapperClass: 'slider-banner__track',
        slideClass: 'slider-banner__slide',
        slidesPerView: 1,
        speed: this.reducedMotion ? 0 : this.speed,
        // Plain loop, no loopAdditionalSlides - Swiper needs enough slides to
        // satisfy that option and quietly misbehaves with only two.
        loop: this.total > 1,
        allowTouchMove: this.total > 1,
        autoplay: false,
        pagination: false,
        a11y: false,
        on: {
          slideChange: this.onSlideChange,
          touchStart: this.clearTimer.bind(this)
        }
      });
    };
  
    Hero.prototype.bind = function () {
      var self = this;
  
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', self.onDotClick);
      });
  
      document.addEventListener('visibilitychange', this.onVisibilityChange);
  
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onBreakpointChange);
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(this.onBreakpointChange);
      }
  
      // Stop burning CPU (and stop the rotation running past the viewer) while
      // the hero is scrolled out of view.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.paused = !entry.isIntersecting;
            if (self.paused) {
              self.clearTimer();
              self.pauseVideos(null);
            } else {
              self.onSlideChange();
            }
          });
        }, { threshold: 0.15 });
  
        this.observer.observe(this.root);
      }
    };
  
    /* --- slide lifecycle ---------------------------------------------------- */
  
    Hero.prototype.activeSlide = function () {
      if (!this.swiper) return null;
      return this.swiper.slides[this.swiper.activeIndex] || null;
    };
  
    Hero.prototype.onSlideChange = function () {
      this.clearTimer();
      this.syncDots();
  
      if (this.paused || document.hidden) return;
  
      var slide = this.activeSlide();
      if (!slide) return;
  
      this.pauseVideos(slide);
  
      var video = slide.querySelector('[data-slider-banner-video]');
      if (video) {
        this.playVideo(video);
      } else {
        this.arm(this.slideDuration(slide));
      }
    };
  
    Hero.prototype.syncDots = function () {
      if (!this.dots.length || !this.swiper) return;
  
      var current = this.swiper.realIndex;
  
      this.dots.forEach(function (dot, index) {
        var isActive = index === current;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };
  
    Hero.prototype.slideDuration = function (slide) {
      var seconds = parseFloat(slide.getAttribute('data-duration'));
      if (!seconds || seconds <= 0) seconds = 5;
      return seconds * 1000;
    };
  
    /* --- video -------------------------------------------------------------- */
  
    // Swiper's loop mode clones slides, so a video is looked up on whichever
    // slide element is currently active rather than cached up front.
    Hero.prototype.pauseVideos = function (exceptSlide) {
      var keep = exceptSlide ? exceptSlide.querySelector('[data-slider-banner-video]') : null;
  
      toArray(this.root.querySelectorAll('[data-slider-banner-video]')).forEach(function (video) {
        if (video === keep) return;
        try {
          video.pause();
          video.currentTime = 0;
        } catch (error) {
          /* Safari can throw before metadata is available. */
        }
      });
    };
  
    Hero.prototype.applySource = function (video) {
      var desktop = video.getAttribute('data-src-desktop');
      var mobile = video.getAttribute('data-src-mobile');
      var src = this.mobileQuery.matches ? mobile || desktop : desktop || mobile;
  
      if (!src || video.getAttribute('src') === src) return;
  
      video.setAttribute('src', src);
      video.load();
    };
  
    Hero.prototype.playVideo = function (video) {
      var self = this;
  
      this.applySource(video);
  
      // With rotation off, or a single slide, the video just loops in place.
      video.loop = !this.autoRotate;
  
      if (!video.dataset.sliderBannerBound) {
        video.dataset.sliderBannerBound = '1';
  
        video.addEventListener('ended', function () {
          if (self.isActive(video)) self.next();
        });
  
        // iOS does not always fire `ended` reliably on inline video.
        video.addEventListener('timeupdate', function () {
          if (!self.isActive(video) || !self.autoRotate) return;
          if (video.duration && video.currentTime >= video.duration - 0.25) self.next();
        });
  
        video.addEventListener('loadedmetadata', function () {
          if (self.isActive(video)) self.armVideoGuard(video);
        });
  
        video.addEventListener('error', function () {
          if (self.isActive(video)) self.arm(self.slideDuration(video.closest('[data-slider-banner-slide]')));
        });
      }
  
      try {
        video.currentTime = 0;
      } catch (error) {
        /* no-op */
      }
  
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        // Autoplay blocked (low power mode, browser policy) - fall back to the
        // slide timer so the carousel never stalls on a frozen frame.
        attempt.catch(function () {
          if (self.isActive(video)) self.arm(self.slideDuration(video.closest('[data-slider-banner-slide]')));
        });
      }
  
      this.armVideoGuard(video);
    };
  
    // Safety net: if neither `ended` nor `timeupdate` gets us to the end, move on
    // shortly after the video should have finished.
    Hero.prototype.armVideoGuard = function (video) {
      var duration = video.duration;
      var ms = duration && isFinite(duration) ? duration * 1000 + 1500 : STALL_GUARD_MS;
      this.arm(ms);
    };
  
    Hero.prototype.isActive = function (video) {
      var slide = this.activeSlide();
      return !!slide && slide.contains(video);
    };
  
    /* --- rotation ----------------------------------------------------------- */
  
    Hero.prototype.arm = function (ms) {
      this.clearTimer();
      if (!this.autoRotate || this.paused || this.editorHold) return;
  
      var self = this;
      this.timer = setTimeout(function () {
        self.next();
      }, ms);
    };
  
    Hero.prototype.clearTimer = function () {
      if (!this.timer) return;
      clearTimeout(this.timer);
      this.timer = null;
    };
  
    Hero.prototype.next = function () {
      this.clearTimer();
      if (!this.autoRotate || this.paused || this.editorHold || !this.swiper) return;
  
      // Swiper turns loop mode off by itself when a slider has too few slides
      // for the mode to work. Rewinding by hand in that case keeps the rotation
      // endless instead of dead-ending on the last slide.
      if (this.swiper.isEnd && !this.swiper.params.loop) {
        this.swiper.slideTo(0);
      } else {
        this.swiper.slideNext();
      }
    };
  
    /* --- events ------------------------------------------------------------- */
  
    Hero.prototype.onDotClick = function (event) {
      var target = parseInt(event.currentTarget.getAttribute('data-slider-banner-dot'), 10);
      if (isNaN(target) || !this.swiper || this.swiper.realIndex === target) return;
      this.clearTimer();
      this.swiper.slideToLoop(target);
    };
  
    Hero.prototype.onVisibilityChange = function () {
      if (document.hidden) {
        this.clearTimer();
        this.pauseVideos(null);
      } else {
        this.onSlideChange();
      }
    };
  
    Hero.prototype.onBreakpointChange = function () {
      var self = this;
      toArray(this.root.querySelectorAll('[data-slider-banner-video]')).forEach(function (video) {
        self.applySource(video);
      });
      this.onSlideChange();
    };
  
    Hero.prototype.goToBlock = function (blockId) {
      if (!this.swiper) return;
  
      var slides = toArray(this.root.querySelectorAll('[data-slider-banner-slide]'));
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-block-id') !== blockId) continue;
        var index = parseInt(slides[i].getAttribute('data-index'), 10);
        if (!isNaN(index)) this.swiper.slideToLoop(index);
        break;
      }
    };
  
    Hero.prototype.destroy = function () {
      this.clearTimer();
      this.pauseVideos(null);
  
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
  
      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
      } else if (this.mobileQuery.removeListener) {
        this.mobileQuery.removeListener(this.onBreakpointChange);
      }
  
      if (this.observer) this.observer.disconnect();
  
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (error) {
          /* no-op */
        }
      }
    };
  
    /* ------------------------------------------------------------------------
       Boot
       ------------------------------------------------------------------------ */
  
    function init(scope) {
      var roots = toArray((scope || document).querySelectorAll('[data-slider-banner]'));
      if (!roots.length) return;
  
      loadSwiper()
        .then(function () {
          roots.forEach(function (root) {
            if (!root.id || instances[root.id]) return;
            instances[root.id] = new Hero(root);
          });
        })
        .catch(function () {
          // No Swiper: leave the hero as a static first slide rather than a
          // broken row of slides.
          roots.forEach(function (root) {
            root.classList.add('is-static');
          });
        });
    }
  
    function destroy(scope) {
      toArray((scope || document).querySelectorAll('[data-slider-banner]')).forEach(function (root) {
        var instance = instances[root.id];
        if (!instance) return;
        instance.destroy();
        delete instances[root.id];
      });
    }
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        init();
      });
    } else {
      init();
    }
  
    /* --- theme editor ------------------------------------------------------- */
  
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  
    document.addEventListener('shopify:section:unload', function (event) {
      destroy(event.target);
    });
  
    document.addEventListener('shopify:block:select', function (event) {
      var root = event.target.closest('[data-slider-banner]');
      if (!root) return;
  
      var instance = instances[root.id];
      if (!instance) return;
  
      // Park on the block being edited, but only for as long as it stays
      // selected - never disable rotation outright, or the slider stays frozen
      // for the rest of the editing session.
      instance.editorHold = true;
      instance.clearTimer();
      instance.goToBlock(event.target.getAttribute('data-block-id'));
    });
  
    function releaseEditorHold(root) {
      if (!root) return;
      var instance = instances[root.id];
      if (!instance || !instance.editorHold) return;
      instance.editorHold = false;
      instance.onSlideChange();
    }
  
    document.addEventListener('shopify:block:deselect', function (event) {
      releaseEditorHold(event.target.closest('[data-slider-banner]'));
    });
  
    // Selecting or leaving the section also counts as leaving the block, and
    // covers the cases where block:deselect does not fire.
    document.addEventListener('shopify:section:select', function (event) {
      releaseEditorHold(event.target.querySelector('[data-slider-banner]'));
    });
  
    document.addEventListener('shopify:section:deselect', function (event) {
      releaseEditorHold(event.target.querySelector('[data-slider-banner]'));
    });
  })();
  